/**
 * API Client for EDX Red
 * Handles API requests with authentication and error handling
 */

// Base API URL - can be configured per environment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

/**
 * Custom fetch function with authentication and error handling
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise} - Promise that resolves to the API response
 */
async function client(endpoint, { body, ...customConfig } = {}) {
  const token = localStorage.getItem('token');
  
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const config = {
    method: body ? 'POST' : 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
  };
  
  if (body) {
    config.body = JSON.stringify(body);
  }
  
  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    
    // Handle 401 Unauthorized by clearing token and redirecting to login
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Your session has expired. Please log in again.');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Something went wrong');
    }
    
    return data;
  } catch (error) {
    // Rethrow error for the calling function to handle
    throw error;
  }
}

/**
 * Make a GET request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} customConfig - Additional config
 * @returns {Promise} - Promise that resolves to the API response
 */
function get(endpoint, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'GET' });
}

/**
 * Make a POST request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} customConfig - Additional config
 * @returns {Promise} - Promise that resolves to the API response
 */
function post(endpoint, body, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'POST', body });
}

/**
 * Make a PUT request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} customConfig - Additional config
 * @returns {Promise} - Promise that resolves to the API response
 */
function put(endpoint, body, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'PUT', body });
}

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - API endpoint
 * @param {Object} customConfig - Additional config
 * @returns {Promise} - Promise that resolves to the API response
 */
function del(endpoint, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'DELETE' });
}

/**
 * Make a multipart POST request to the API (for file uploads)
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with files
 * @param {Object} customConfig - Additional config
 * @returns {Promise} - Promise that resolves to the API response
 */
async function upload(endpoint, formData, customConfig = {}) {
  const token = localStorage.getItem('token');
  
  const headers = {};
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const config = {
    method: 'POST',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
    body: formData,
  };
  
  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Your session has expired. Please log in again.');
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Something went wrong');
    }
    
    return data;
  } catch (error) {
    throw error;
  }
}

export { get, post, put, del, upload };